import { NcwOnboardingRecoveryImage, useIntl, useSettings } from '@capsicum/common';
import { Button, Image, Text, useRouter } from '@capsicum/common-web';
import { type V2MigrationTxExecErrorModalProps } from '@capsicum/features/nc-wallet/shared';
import { useCallback } from 'react';
import styled from 'styled-components';

export const V2MigrationTxExecErrorModal = ({ close }: V2MigrationTxExecErrorModalProps) => {
	const { $t } = useIntl();
	const { push } = useRouter();

	const {
		areSettingsLoading,
		settings: { contactUsHereLink }
	} = useSettings();

	const onContactCustomerSupport = useCallback(() => window.open(contactUsHereLink, '_blank'), [contactUsHereLink]);

	const onRecoverPasskey = useCallback(() => {
		push('/settings/recover-wallet');
		close();
	}, [push, close]);

	return (
		<S.Wrapper>
			<S.Header>
				<S.StyledImage alt="Generate Recovery" height={132} src={NcwOnboardingRecoveryImage} />

				<S.StyledText variant="title6">
					{$t('v2FTMigrationInitFlow.upgrade.modals.txExecError.title')}
				</S.StyledText>
			</S.Header>

			<S.Content>
				<S.StyledText variant="text3">
					{$t('v2FTMigrationInitFlow.upgrade.modals.txExecError.descriptions.one')}
				</S.StyledText>

				<S.StyledText variant="text3">
					{$t('v2FTMigrationInitFlow.upgrade.modals.txExecError.descriptions.two')}
				</S.StyledText>
			</S.Content>

			<S.Actions>
				<Button
					fullWidth
					testID="v2MigrationTxExecErrorModal-recoverPasskey-button"
					text={$t('v2FTMigrationInitFlow.upgrade.modals.txExecError.actions.recoverPasskey')}
					onClick={onRecoverPasskey}
				/>

				<Button
					fullWidth
					testID="v2MigrationTxExecErrorModal-tryAgain-button"
					text={$t('global.button.retry')}
					variant="outlined"
					onClick={close}
				/>

				<Button
					color="neutral"
					fullWidth
					isLoading={areSettingsLoading}
					testID="v2MigrationTxExecErrorModal-contactSupport-button"
					text={$t('v2FTMigrationInitFlow.upgrade.modals.txExecError.actions.contactCustomerSupport')}
					variant="text"
					onClick={onContactCustomerSupport}
				/>
			</S.Actions>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const Header = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxlarge};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Actions = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const StyledText = styled(Text)`
		text-align: center;
	`;

	export const StyledImage = styled(Image)`
		width: 100%;
		object-fit: contain;
	`;
}
